import {Component, computed, inject, signal} from '@angular/core';
import {finalize, of, take, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {TranslocoDirective} from '@jsverse/transloco';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {LucideAngularModule} from 'lucide-angular';
import {toSignal} from '@angular/core/rxjs-interop';
import {AltchaComponent} from '@shared/altcha.component';
import {SpinnerComponent} from '@shared/spinner/spinner.component';
import {PublicHostApi} from '@domain/host/public-host.api';
import {HostDTO} from '@domain/host/host.dto';
import {AuthService} from '@core/auth/auth.service';
import {User} from '@domain/user/user';
import {ValidateEmailRequest} from '@core/auth/auth.interfaces';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';

@Component({
  selector: 'app-auth-validate-host-email',
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    LucideAngularModule,
    AltchaComponent,
    SpinnerComponent,
    RouterLink
  ],
  templateUrl: './validate-host-email.component.html',
  styleUrl: './validate-host-email.component.scss'
})
export class ValidateHostEmailComponent {

  private static readonly VALIDATE_EMAIL_DELAY = 36_000_000; // 10 hours
  private static readonly QUERY_PARAM_NOW = 'now';
  private static readonly QUERY_PARAM_DATA = 'data';
  private static readonly QUERY_PARAM_HASH = 'hash';
  private static readonly QUERY_PARAM_SECRET = 'secret';

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly publicHostApi = inject(PublicHostApi);
  private readonly data = toSignal(this.route.data);
  public readonly host = computed(() => (this.data()?.host as HostDTO) ?? null);
  private readonly authService = inject(AuthService);

  public authenticatedUser = signal<User | null>(null);
  public needsValidation = computed(() => {
    const h = this.host();
    return h?.newEmail !== null;
  });

  public doneValidation = signal(false);
  public isValidated = computed(() => {
    const h = this.host();
    return h?.newEmail === null || this.doneValidation();
  });

  private isNotARobot = signal(false);

  public showCaptcha = computed(() => {
    return this.needsValidation() && !this.authenticatedUser() && this.getValidationParams() && !this.isExpiredLink();
  })

  public showMissingParams = computed(() => {
    return this.needsValidation() && !this.getValidationParams() && !this.isExpiredLink();
  });

  public isValidationError = signal(false);
  public captchaForm = this.fb.group({
    captcha: ['', Validators.required]
  });
  public validating = signal(false);


  public readonly routes = {
    hostPreview: RouteGenerator.abs(UI_ROUTES.account.host.home, {hostId: this.host()?.id!}),
    hostDashboard: RouteGenerator.abs(UI_ROUTES.account.host.dashboard),
    login: RouteGenerator.abs(UI_ROUTES.auth.login),
  };

  public constructor() {
    const user = this.authService.getAuthenticatedUser();
    this.authenticatedUser.set(user);
    if (user && this.needsValidation()) {
      this.validateHost();
    }
  }

  public validateHost(): void {
    const host = this.host();
    if (!host) {
      return;
    }
    this.isNotARobot.set(true);
    const validateParams = this.getValidationParams();
    if (!validateParams) {
      return;
    }
    const validateRequest = {... validateParams};
    if (!this.authenticatedUser()) {
      const captcha = this.captchaForm?.get('captcha')?.value ?? null;
      if (!captcha) {
        return;
      }
      validateRequest.captcha = captcha;
    }
    this.validating.set(true);
    this.publicHostApi.validateEmail(host, validateRequest).pipe(
      tap(res => {
        if (res) {
          this.doneValidation.set(true);
        }
      }),
      take(1),
      catchError(_err => {
        this.isValidationError.set(true);
        return of(false);
      }),
      finalize(() => {
        this.validating.set(false);
      })
    ).subscribe(_ => {});
  }

  private getDataParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateHostEmailComponent.QUERY_PARAM_DATA] ?? null;
  }

  private getHashParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateHostEmailComponent.QUERY_PARAM_HASH] ?? null;
  }

  private getSecretParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateHostEmailComponent.QUERY_PARAM_SECRET] ?? null;
  }

  private getStringTimeParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateHostEmailComponent.QUERY_PARAM_NOW] ?? null;
  }

  private getIntTime(): number | null {
    const time = this.getStringTimeParam();
    if (!time) {
      return null;
    }
    const num = parseInt(time, 10);
    if (isNaN(num)) {
      return null;
    }
    return num;
  }

  private isExpiredLink(): boolean {
    const t = this.getIntTime();
    if (!t) {
      return false;
    }
    return t + ValidateHostEmailComponent.VALIDATE_EMAIL_DELAY < Date.now();
  }

  private validationParams: ValidateEmailRequest | null | undefined = undefined;
  private getValidationParams(): ValidateEmailRequest | null {
    if (this.validationParams !== undefined) {
      return this.validationParams;
    }
    const time = this.getIntTime();
    const data = this.getDataParam();
    const secret = this.getSecretParam();
    const hash = this.getHashParam();
    this.validationParams = (time && data && secret && hash && !this.isExpiredLink())
      ? { data: data, secret: secret, now: time.toString(10), hash: hash }
      : null;
    return this.validationParams;
  }
}
